import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaExclamationCircle, FaCalendarAlt, FaArrowLeft, FaTimes } from 'react-icons/fa';
import TenantSidebar from './TenantSidebar';
import TenantNavbar from './TenantNavbar';
import LoadingScreen from '../PropertyManager/LoadingScreen';
import '../PropertyManager/Sidebar.css';
import '../PropertyManager/Navbar.css';
import './TenantPaymentHistory.css';
import Tutorial from '../PropertyManager/Tutorial';

const BASE_URL = 'https://api.rside.app';

const TPH_STEPS = [
  {
    title: 'Payment History',
    description: 'See all your past rent payments and your current balance here.',
  },
  {
    title: 'Account Summary',
    description: 'Your monthly rent amount and any outstanding balance are shown here.',
    anchorId: 'tph-summary',
    cardAlignment: 'bottom',
  },
  {
    title: 'Payment Records',
    description: 'All payments are listed here, grouped by month with dates and amounts.',
    anchorId: 'tph-history',
    cardAlignment: 'top',
  },
];

function fmt$(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function TenantPaymentHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('');
  const [initials, setInitials] = useState('');
  const [rentData, setRentData] = useState(null);
  const [payHistory, setPayHistory] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate('/tenant/login'); return; }
      try {
        const sessionRes = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
        const session = await sessionRes.json();
        const name = session?.tenant?.full_name || '';
        setTenantName(name);
        setInitials(name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2));
        setUserEmail(user.email);
        const doorId = session?.tenant?.door_id;
        if (doorId) {
          const [rentRes, histRes] = await Promise.all([
            fetch(`${BASE_URL}/GetPastDuePaymentsByDoor?door_id=${doorId}`),
            fetch(`${BASE_URL}/TenantApp/ListPaymentHistory?Door_id=${doorId}`),
          ]);
          const data = await rentRes.json();
          setRentData(data);
          try {
            const histData = await histRes.json();
            setPayHistory(Array.isArray(histData) ? histData : (histData?.payments || histData?.history || []));
          } catch { setPayHistory([]); }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  if (loading) return <LoadingScreen />;

  const summary = rentData?.summary;
  const lease = rentData?.lease;
  const monthlyRent = summary?.monthly_rent || lease?.Rent || 0;
  const pastDue = Number(summary?.past_due ?? 0);
  const isPastDue = pastDue > 0;

  const getPayDate  = (p) => p.paydate || p.Paydate || p.Pay_Date || p.pay_date || p.Payment_Date || p.payment_date || p.date_paid || p.DatePaid || p.Date || p.date || p.Created_At || p.created_at || '';
  const getPayAmt   = (p) => Number(p.amount || p.Amount || 0);
  const getMethod   = (p) => p.Method || p.method || p.Payment_Method || p.payment_method || '';
  const getSource   = (p) => p.Source || p.source || '';
  const getNotes    = (p) => p.Notes || p.notes || p.note || p.Note || '';
  const getPartial  = (p) => !!(p.partial_Payment || p.Partial_Payment || p.partial_payment || p.is_partial || p.IsPartial);
  const getMonthOf  = (p) => {
    const mo = (p.MonthOf || p.Month_Of || p.month_of || p.monthOf || '').trim();
    if (mo) return mo;
    const raw = getPayDate(p);
    if (!raw) return '—';
    const d = new Date(raw);
    return isNaN(d) ? '—' : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  const getPayLabel = (p) => {
    const cat = (p.Category || p.category || '').toLowerCase();
    const typ = (p.Type || p.type || '').toLowerCase();
    if (cat === 'fee' || typ === 'fee') return 'Fee Payment';
    if (cat === 'rent_forgiveness' || typ === 'rent_forgiveness') return 'Forgiveness';
    return 'Rent Payment';
  };

  const sortedHistory = [...payHistory].sort((a, b) => new Date(getPayDate(b)) - new Date(getPayDate(a)));

  const byMonth = sortedHistory.reduce((acc, p) => {
    const raw = getPayDate(p);
    let key = 'Unknown';
    if (raw) {
      const d = new Date(raw);
      if (!isNaN(d)) key = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const sortedMonths = Object.entries(byMonth).sort(([a], [b]) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return new Date(b) - new Date(a);
  });

  return (
    <div className="home-page">
      <TenantSidebar initials={initials} />
      <div className="main-content">
        <TenantNavbar title="Payment History" tenantName={tenantName} initials={initials} />

        <div className="tph-body">

          <button className="tph-back-btn" onClick={() => navigate('/tenant/payments')}>
            <FaArrowLeft style={{ fontSize: 13 }} /> Back to Payments
          </button>

          <div id="tph-summary" className="tph-summary-row">
            <div className="tph-stat-card">
              <span className="tph-stat-label">Monthly Rent</span>
              <span className="tph-stat-value">{fmt$(summary?.monthly_rent || lease?.Rent)}</span>
              <span className="tph-stat-sub">
                due on the {lease?.Rent_Due || 1}{lease?.Rent_Due === 1 ? 'st' : lease?.Rent_Due === 2 ? 'nd' : lease?.Rent_Due === 3 ? 'rd' : 'th'} of each month
              </span>
            </div>
            <div className={`tph-stat-card ${isPastDue ? 'tph-stat-pastdue' : ''}`}>
              <span className="tph-stat-label">Outstanding Balance</span>
              <span className={`tph-stat-value ${isPastDue ? 'tph-red' : 'tph-green'}`}>
                {isPastDue ? fmt$(pastDue) : 'All clear'}
              </span>
              {isPastDue && <span className="tph-pastdue-pill">OVERDUE</span>}
            </div>
          </div>

          {lease && (
            <div className="tph-lease-bar">
              <FaCalendarAlt style={{ color: '#2563eb', fontSize: 13 }} />
              <span>Lease period:</span>
              <strong>{summary?.lease_start || lease.Start_Date}</strong>
              <span>→</span>
              <strong>{summary?.lease_end || lease.End_Date}</strong>
              <span className="tph-lease-sep">·</span>
              <span>Rent due on the <strong>{lease.Rent_Due}{lease.Rent_Due === 1 ? 'st' : lease.Rent_Due === 2 ? 'nd' : lease.Rent_Due === 3 ? 'rd' : 'th'}</strong> of each month</span>
            </div>
          )}

          <div id="tph-history" className="tph-history-card">
            <h3 className="tph-history-title">Payment History</h3>

            {payHistory.length === 0 ? (
              <p className="tph-empty">No payments recorded yet.</p>
            ) : (
              <div className="tph-month-groups">
                {sortedMonths.map(([month, payments]) => {
                  const monthTotal = payments.reduce((s, p) => s + getPayAmt(p), 0);
                  return (
                    <div key={month} className="tph-month-group">
                      <div className="tph-month-header">
                        <span className="tph-month-name">{month}</span>
                        <span className="tph-month-total">{fmt$(monthTotal)}</span>
                      </div>
                      {payments.map((p, i) => (
                        <div key={p.id || i} className="tph-payment-row tph-payment-row-clickable" onClick={() => setSelectedPayment(p)}>
                          <div className="tph-payment-icon"><FaCheckCircle /></div>
                          <div className="tph-payment-info">
                            <span className="tph-payment-label">{getPayLabel(p)}</span>
                            {getPayDate(p) && <span className="tph-payment-date">{fmtDate(getPayDate(p))}</span>}
                          </div>
                          <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2,flexShrink:0}}>
                            <span className="tph-payment-amount">{fmt$(getPayAmt(p))}</span>
                            {getMethod(p) && <span style={{fontSize:11,color:'#9ca3af',fontWeight:500}}>{getMethod(p)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {isPastDue && (
              <div className="tph-pastdue-banner">
                <FaExclamationCircle style={{ fontSize: 16, flexShrink: 0 }} />
                <div>
                  <p className="tph-pastdue-title">Balance due: {fmt$(pastDue)}</p>
                  <p className="tph-pastdue-sub">Please contact your property manager to resolve your outstanding balance.</p>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
      <Tutorial
        steps={TPH_STEPS}
        storageKey={`hasSeenPayHistoryTutorial_${userEmail}`}
      />

      {selectedPayment && (
        <div className="tph-modal-overlay" onClick={() => setSelectedPayment(null)}>
          <div className="tph-modal-card" onClick={e => e.stopPropagation()}>
            <div className="tph-modal-hd">
              <span className="tph-modal-hd-title">{getPayLabel(selectedPayment)}</span>
              <button className="tph-modal-close" onClick={() => setSelectedPayment(null)}><FaTimes /></button>
            </div>
            <div className="tph-modal-amount-wrap">
              <span className="tph-modal-amt-lbl">Amount</span>
              {getMethod(selectedPayment) && <span className="tph-modal-method-badge">{getMethod(selectedPayment)}</span>}
              <div className="tph-modal-amount">{fmt$(getPayAmt(selectedPayment))}</div>
            </div>
            <div className="tph-modal-fields">
              <div className="tph-modal-field">
                <span className="tph-modal-field-lbl">Partial Payment</span>
                <span className="tph-modal-field-val">{getPartial(selectedPayment) ? 'Yes' : 'No'}</span>
              </div>
              <div className="tph-modal-field">
                <span className="tph-modal-field-lbl">Method</span>
                <span className="tph-modal-field-val">{getMethod(selectedPayment) || '—'}</span>
              </div>
              <div className="tph-modal-field">
                <span className="tph-modal-field-lbl">Month Of</span>
                <span className="tph-modal-field-val">{getMonthOf(selectedPayment)}</span>
              </div>
              <div className="tph-modal-field">
                <span className="tph-modal-field-lbl">Payment Date</span>
                <span className="tph-modal-field-val">{getPayDate(selectedPayment) ? fmtDate(getPayDate(selectedPayment)) : '—'}</span>
              </div>
              <div className="tph-modal-field">
                <span className="tph-modal-field-lbl">Category</span>
                <span className="tph-modal-field-val">{selectedPayment.Category || selectedPayment.category || '—'}</span>
              </div>
              <div className="tph-modal-field">
                <span className="tph-modal-field-lbl">Source</span>
                <span className="tph-modal-field-val">{getSource(selectedPayment) || '—'}</span>
              </div>
              {getNotes(selectedPayment) && (
                <div className="tph-modal-field tph-modal-field-full">
                  <span className="tph-modal-field-lbl">Notes</span>
                  <span className="tph-modal-field-val">{getNotes(selectedPayment)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantPaymentHistory;
